<template>
  <div id="maintenanceView">
    <Loader v-if="loading" />
    <v-card v-else>
      <v-toolbar elevation="0">
        <v-btn icon @click="routeToRequestList">
          <v-icon size="40">mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('MaintenanceView.MaintenanceRequestDetails') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn v-if="isAbleToEdit" color="primary" @click="openRequestEditDlg">
          {{$t("GeneralTranslations.Edit")}}
        </v-btn>
      </v-toolbar>
      <v-container>
        <v-row>
          <v-col cols="3" v-if="request.residentName"> <b>Resident Name: </b> </v-col
          ><v-col cols="9" v-if="request.residentName">{{ request.residentName }} </v-col>

          <v-col cols="3"><b >Request Details: </b> </v-col>
          <v-col cols="9"> {{ request.name }} </v-col>

          <v-col cols="3"><b>{{ $t('MaintenanceView.RequestDate') }}:</b></v-col>
          <v-col cols="9">{{new Date(request.dateTime.seconds * 1000) | localizedDate }}</v-col>
          
          <v-col cols="3" v-if="request.scheduledMaintenanceId" class="d-flex align-center"> <span class="red--text"><b>Scheduled Maintenance </b> </span></v-col
          ><v-col cols="9" v-if="request.scheduledMaintenanceId"> <v-btn class="primary" @click="openScheduledMaintenance()">View</v-btn> </v-col>

          <v-col cols="3" v-if="request.priority">  <b class="mr-1">Priority:</b> </v-col>
          <v-col cols="9" v-if="request.priority"> <span :class="getColor(request.priority)">{{ request.priority }}</span></v-col>
          
          <v-col cols="3" v-if="request.estimatedCompletionDate">  <b class="mr-1">Est. Completion Date:</b> </v-col>
          <v-col cols="9" v-if="request.estimatedCompletionDate"> <span>{{ request.estimatedCompletionDate | localizedDate(facility.dateFormat) }}</span></v-col>

          <v-col cols="3" class="d-flex align-center">  <b class="mr-1">Assigned To:</b> </v-col>
          <v-col cols="9" class="d-flex align-center">
            <span class="mr-3">{{ request.assignee ? request.assignee : '-' }}</span>
            <v-btn v-if="!isNewRequest" color="primary" @click="isReassignRequest = !isReassignRequest">
              Reassign
            </v-btn>
          </v-col>

          <v-col cols="3"> <b>Room / Location:</b></v-col>
          <v-col cols="9">{{ (request.roomName ? request.roomName : '-') + ' / ' + (request.location ? request.location : '-')}}</v-col>

          <v-col cols="3"> <b> {{ $t('GeneralTranslations.Status') }}:</b> </v-col>
          <v-col cols="9"> {{ getStatus(request.status) }} </v-col>

          <v-col cols="3"><b class="mr-1">{{ $t('GeneralTranslations.Category') }}:</b></v-col>
          <v-col cols="9">
            <span v-if="checkExistCategory(request.category)">{{ $t(`Maintenance.Category.${convertToTranslationKey(request.category)}`) }}</span>
            <span v-else>
              <span v-if="request.category"> {{ request.category }} </span>
              <span v-else>-</span>
            </span>
          </v-col>

          <v-col cols="3">  <b class="mr-1">{{ $t('GeneralTranslations.Description') }}:</b> </v-col>
          <v-col cols="9"> <span v-if="request.description">{{ request.description }}</span> <span v-else>-</span></v-col>

          <v-col cols="3">  <b class="mr-1">{{ $t('GeneralTranslations.Comment') }}:</b> </v-col>
          <v-col cols="9"><span v-if="request.comment">{{ request.comment }}</span> <span v-else>-</span></v-col>

          <v-col cols="3" v-if="isCompleted || isCanceled">  <b class="mr-1">{{ $t('MaintenanceView.AdditionalInformationForTheResident') }}:</b> </v-col>
          <v-col cols="9" v-if="isCompleted || isCanceled"><span v-if="request.additionalResponse">{{ request.additionalResponse }}</span> <span v-else>-</span></v-col>

          <v-col cols="3" v-if="isCompleted || isCanceled">  <b class="mr-1">Completion Date:</b> </v-col>
          <v-col cols="9" v-if="isCompleted || isCanceled"> <span v-if="request.completedDateTime"> {{new Date(request.completedDateTime.seconds * 1000) | localizedDate }}</span></v-col>

          <v-col cols="12" v-if="request?.photoUrls?.length">
            <b>Photos:</b>
          </v-col>

          <v-row v-if="request?.photoUrls?.length" class="px-4">
            <v-col
              cols="3"
              v-for="(photo, index) in request.photoUrls"
              :key="index"
              class="maintenance-request-photo"
            >
              <img :src="photo" alt="">
            </v-col>
          </v-row>
        </v-row>
      </v-container>

      <v-container v-if="request.status === MaintenanceRequestStatus.New || isReassignRequest">
        <v-divider></v-divider>

        <validation-observer ref="observer" v-slot="{ invalid }">
          <v-form @submit.prevent="assignRequest">
            <v-toolbar-title>Triage Request</v-toolbar-title>
            <p>Assign to a member of the maintenance team.</p>

            <v-row>
              <v-col md-6 sm-12 class="pb-0">
                <b class="ml-3">Maintenance Technician:</b>
                <validation-provider v-slot="{ errors }" name="MaintenanceTechnician" rules="required">
                  <v-select
                    v-model="selectedTechnician"
                    :items="MaintenanceTechnicians"
                    item-text="name"
                    item-value="email"
                    :error-messages="errors"
                    required
                  ></v-select>
                </validation-provider>
              </v-col>

              <v-col md-6 sm-12 class="pb-0">
                <b class="ml-3">Priority:</b>
                <validation-provider v-slot="{ errors }" name="Priority" rules="required">
                  <v-select
                    v-model="ticketPriority"
                    :items="ticketPriorityOptions"
                    item-text="name"
                    item-value="name"
                    :error-messages="errors"
                    required
                  ></v-select>
                </validation-provider>
              </v-col>

              <v-col md-6 sm-0 class="pb-0">
                <b class="mr-1">Expected Completion Date:</b>
                <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="estimatedDeliveryDateFormatted"
                        label=""
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="estimatedDeliveryDate" no-title scrollable>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="menu = false">
                      Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="$refs.menu.save(date)">
                      OK
                    </v-btn>
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row>
              <v-col md-4 sm-12 class="email-request-to-vendor pt-0">
                <v-checkbox
                  v-model="request.isSendEmailToVendor"
                  :label="$t('MaintenanceSchedule.EmailRequestToVendor')"
                ></v-checkbox>
                <span>{{ $t('MaintenanceSchedule.EmailRequestToVendorText') }}</span>
              </v-col>
            </v-row>
            <v-row class="mt-0">
              <v-col md-3>
                <v-btn
                  type="submit"
                  color="primary"
                  text
                  :disabled="invalid"
                >
                  Assign Request
                </v-btn>
                <v-btn color="primary" text @click="cancelRequest">
                  Cancel Request
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </validation-observer>
      </v-container>

      <v-divider></v-divider>

      <v-row v-if="request.status === MaintenanceRequestStatus.InProgress" v-show="!isReassignRequest">
        <v-col md="6">
          <v-list three-line subheader>
            <v-list-item>
              <v-textarea
                class="pa-2"
                v-model="additionalResponse"
                rows="5"
                :label="$t('MaintenanceView.AdditionalInformationForTheResident')"
                required
              ></v-textarea>
            </v-list-item>
            <v-list-item>
              <v-list-item-action>
                <div class="container">
                  <v-btn color="primary" @click="completeRequest">
                    {{ $t('MaintenanceView.CompleteRequest') }}
                  </v-btn>
                  <v-btn color="primary" class="ml-4" @click="cancelRequest">
                    Cancel Request
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>

      <MaintenanceRequestCancelDlg ref="maintenanceRequestCancelDlg"/>

      <MaintenanceRequestEditDlg ref="maintenanceRequestEditDlg"/>
    </v-card>
  </div>
</template>

<script>
import { toTranslationKey } from '@/utils/stringFormat';
import { maintenanceCategoriesKeys } from '@/consts/MaintenanceCategoriesKeys';
import { MaintenanceMixin } from '@/mixins/Maintenance';
import Loader from "@/components/loader/Loader.vue";
import MaintenanceRequestCancelDlg from "@/views/maintenance/maintenanceRequestCancelDlg/maintenanceRequestCancelDlg.vue";
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {MaintenancePriority} from "@/enums/MaintenancePriority";
import {dateFormats} from "@/consts/DateTimeFormats";
import moment from "moment";
import MaintenanceRequestEditDlg from "@/views/maintenance/maintenanceRequestEditDlg/MaintenanceRequestEditDlg.vue";

export default {
  name: 'MaintenanceView',
  components: {Loader, MaintenanceRequestCancelDlg, ValidationObserver, ValidationProvider, MaintenanceRequestEditDlg},
  mixins: [MaintenanceMixin],
  data() {
    return {
      loading: true,
      additionalResponse: '',
      selectedTechnician: null,
      ticketPriority: null,
      estimatedDeliveryDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menu: false,
      isReassignRequest: false
    };
  },
  computed: {
    MaintenanceRequestStatus() {
      return MaintenanceRequestStatus
    },
    request() {
      return this.$store.state.maintenanceModule.currentMaintenanceRequest;
    },
    MaintenanceTechnicians() {
      return [...this.$store.state.userModule.maintenanceUsers, ...this.$store.state.userModule.maintenanceVendors.map(x => { return {...x, isVendor: true} })];
    },
    isNewRequest() {
      return this.request.status === this.MaintenanceRequestStatus.New;
    },
    ticketPriorityOptions() {
      return Object.values(MaintenancePriority);
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    estimatedDeliveryDateFormatted() {
      const dateFormat = dateFormats.find(x => x.format === this.facility.dateFormat);
      return moment(this.estimatedDeliveryDate).format(dateFormat ? dateFormat.name : dateFormats[0].name);
    },
    isAbleToEdit() {
      return [MaintenanceRequestStatus.New, MaintenanceRequestStatus.InProgress].includes(this.request?.status);
    },
    isCompleted() {
      return this.request.status === 'complete';
    },
    isCanceled() {
      return this.request.status === 'cancelled';
    }
  },
  async created() {
    await this.$store.dispatch('getMaintenanceRequest', this.$route.params.id);
    await this.$store.dispatch('getMaintenanceUsers');
    await this.$store.dispatch("getMaintenanceVendors");
    await this.$store.dispatch('getMaintenanceTypes');
    await this.$store.dispatch('getMaintenanceCategories');

    this.selectedTechnician = this.request.assigneeEmail ?? null;
    this.ticketPriority = this.request.priority ?? null;
    this.estimatedDeliveryDate = this.request.estimatedCompletionDate ?? this.estimatedDeliveryDate;
    this.loading = false;
  },
  methods: {
    convertToTranslationKey(text) {
      return toTranslationKey(text);
    },
    routeToRequestList() {
      if(!this.isNewRequest) {
        this.$router.push('/maintenanceRequests?tab=2');
      } else {
        this.$router.push('/maintenanceRequests');
      }
    },
    async completeRequest() {
      await this.$store.dispatch('completeMaintenanceRequest', {
        id: this.$route.params.id,
        additionalResponse: this.additionalResponse,
      });
    },
    async assignRequest() {
      await this.$store.dispatch('assignMaintenanceRequest', {
        id: this.$route.params.id,
        assignee: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.name ?? null,
        assigneeId: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.id ?? null,
        assigneeEmail: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.email ?? null,
        estimatedCompletionDate: this.estimatedDeliveryDate,
        priority: this.ticketPriority,
        isSendEmailToVendor: this.request.isSendEmailToVendor ?? null
      });
    },
    getColor(priority){
       if(priority == 'Urgent' || priority == 'High'){
        return 'red--text'
       }else if(priority == 'Medium'){
        return 'orange--text'
       }else{
        return 'green--text'
       }

    },
    checkExistCategory(category) {
      const key = toTranslationKey(category);
      return maintenanceCategoriesKeys.includes(key);
    },
    openScheduledMaintenance() {
      this.$router.push("/scheduledMaintenance/" + this.request.scheduledMaintenanceId)
    },
    cancelRequest() {
      this.$refs.maintenanceRequestCancelDlg.open(this.request.id)
    },
    getStatus(status) {
      switch (status) {
        case MaintenanceRequestStatus.New: {
          return this.$t("MaintenanceList.New");
        }
        case MaintenanceRequestStatus.InProgress: {
          return this.$t("MaintenanceList.InProgress");
        }
        case MaintenanceRequestStatus.Cancelled: {
          return this.$t("MaintenanceList.Cancelled");
        }
        case MaintenanceRequestStatus.Complete: {
          return this.$t("MaintenanceList.Completed");
        }
      }
    },
    openRequestEditDlg() {
      this.$refs.maintenanceRequestEditDlg.open(this.request);
    }
  },
};
</script>

<style lang="scss" scoped>
.maintenance-request-photo {
  img {
    width: 100%;
    height: 300px;
  }
}

::v-deep.email-request-to-vendor {
  .v-messages {
    display: none !important;
  }
}
</style>
